import { createRef, useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { MdAdd, MdCode, MdEdit } from "react-icons/md";
import { useSelector } from "react-redux";

import PRButton from "~components/Generic/PRButton";
import PRContainer from "~components/Generic/PRContainer";
import PRTable from "~components/Generic/PRTable";
import LowCodeEditorModal from "~components/LowCodeEditor";
import { apiUrlStatisticsFigures, lowCodeModuleType } from "~constants";
import { selectCurrentProject, selectUserInfo } from "~store/user/selectors";

const StatisticsFigureList = () => {
  const { t } = useTranslation();
  const tableRef = createRef();
  const currentProject = useSelector(selectCurrentProject);
  const userInfo = useSelector(selectUserInfo);

  const handleClickEditGenerator = useCallback(
    (item) => async () => {
      const readOnly = !userInfo?.is_superuser && item.is_public;

      await LowCodeEditorModal.show({
        moduleType: lowCodeModuleType.FIGURES_MODULES,
        figureId: item.id,
        hideContext: true,
        defaultSelectedProcedureName: "main",
        showSidebarAction: true,
        readOnly,
      });
      tableRef.current?.refresh();
    },
    [tableRef, userInfo?.is_superuser]
  );
  const columns = useMemo(() => {
    return [
      {
        label: t("common.id"),
        key: "id",
      },
      {
        label: t("dashboard.statisticsFigureList.figureName"),
        key: "figure_name",
      },
      {
        label: t("dashboard.statisticsFigureList.refreshFrequency"),
        key: "refresh_frequency",
        render: (row) => row.refresh_frequency + " " + t("dashboard.statisticsFigureList.seconds"),
      },
      {
        label: t("dashboard.statisticsFigureList.isPublic"),
        key: "is_public",
        render: (row) => (row.is_public ? t("common.yes") : t("common.no")),
      },
      {
        label: t("dashboard.statisticsFigureList.isActive"),
        key: "is_active",
        render: (row) => (row.is_active ? t("common.yes") : t("common.no")),
      },
      {
        label: t("dashboard.statisticsFigureList.userSpecific"),
        key: "user_spesific",
        render: (row) => (row.user_spesific ? t("common.yes") : t("common.no")),
      },
      {
        label: t("common.actions"),
        key: "actions",
        actions: true,
        render: (row) => {
          const handleClickDelete = () => {};
          return (
            <div className="d-flex justify-content-center">
              <PRButton
                outline
                color="primary"
                icon={MdEdit}
                link={`/stats/settings/figures/form/${row.id}`}
                size="sm"
                tooltipText={t("common.edit")}
              />
              <PRButton
                outline
                className="ms-1"
                color="primary"
                icon={MdCode}
                size="sm"
                tooltipText={t("dashboard.statisticGenerator.editorTooltip")}
                onClick={handleClickEditGenerator(row)}
              />
              {/* <PRButton
                outline
                className="ms-1"
                color="danger"
                icon={MdDelete}
                size="sm"
                tooltipText={t("common.delete")}
                onClick={handleClickDelete}
              /> */}
            </div>
          );
        },
      },
    ];
  }, [t]);

  const actions = useMemo(() => {
    return [
      {
        label: t("dashboard.statisticsFigureList.createFigure"),
        icon: MdAdd,
        color: "success",
        link: "/stats/settings/figures/form/",
      },
    ];
  }, [t]);

  const parentName = [
    {
      label: t("dashboard.statisticsFigure"),
    },
  ];

  return (
    <PRContainer
      actions={actions}
      description={t("dashboard.statisticGenerator.description")}
      name={t("common.statistics")}
      parentName={parentName}
    >
      <div className="mt-2">
        <PRTable ref={tableRef} columns={columns} url={apiUrlStatisticsFigures.get.format(currentProject?.id)} />
      </div>
    </PRContainer>
  );
};

export default StatisticsFigureList;
